import { useState } from "react";
import { Button, Col, Row } from "reactstrap";
import PlaylistData from "../playlist/data/PlaylistData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faUsers } from "@fortawesome/free-solid-svg-icons";
import { CrewTitle } from "../common/components/CrewTitle";
import { SettingsModal } from "../common/components/multiselectablelist/settings/SettingsModal";
import { CrewDialog } from "../crew/CrewDialog";
import { HeaderButtonBurger } from "../common/header/HeaderButtonBurger";
import { CyPlaylistIndex } from "./CyPlaylistIndex";
import { CyApp } from "../CyApp";
import { CrewAwareItemsLink } from "./CrewAwareItemsLink";
import { Column } from "../common/components/multiselectablelist/Column";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

interface Props {
  changeOrderBy: (newField: keyof PlaylistData) => void;
  showDetailsInPlaylistSummary: boolean;
  orderByAscOrDesc: "asc" | "desc";
  orderByField: keyof PlaylistData;
  currentCrewIdentifier: string | undefined;
  setCurrentCrewIdentifier: (i: string | undefined) => void;
  setShowDetailsInPlaylistSummary: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
  amountOfEntriesInPlaylistIndex: number;
  setAmountOfEntriesInPlaylistIndex: (
    value: ((prevState: number) => number) | number,
  ) => void;
  setIsMainMenuOpen: (
    value: ((prevState: boolean) => boolean) | boolean,
  ) => void;
}

export function PlaylistIndexHeaderFirstRow({
  changeOrderBy,
  showDetailsInPlaylistSummary,
  orderByAscOrDesc,
  orderByField,
  currentCrewIdentifier,
  setCurrentCrewIdentifier,
  setShowDetailsInPlaylistSummary,
  amountOfEntriesInPlaylistIndex,
  setAmountOfEntriesInPlaylistIndex,
  setIsMainMenuOpen,
}: Props) {
  const [playlistAdvancedMenuOpen, setPlaylistAdvancedMenuOpen] =
    useState<boolean>(false);
  const [isCrewDialogOpen, setIsCrewDialogOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const columns: Column<PlaylistData>[] = useMemo(
    () => [
      { title: t("playlist.tableTitle"), field: "titleLowercase" },
      { title: t("common.table.created"), field: "createdAt" },
      { title: t("common.table.updated"), field: "updatedAt" },
    ],
    [t],
  );

  return (
    <>
      <Row className="gy-2 pt-2">
        <Col className="col-auto d-flex order-1">
          <div className="d-flex with-left-column">
            <HeaderButtonBurger setIsMainMenuOpen={setIsMainMenuOpen} />
          </div>
        </Col>
        <Col className="d-flex col-8 col-md order-3 order-md-2 align-items-center ps-1">
          <h3
            className="mb-0 page-header-title"
            data-cy={CyPlaylistIndex.PlaylistPageHeader}
          >
            <CrewTitle currentCrewIdentifier={currentCrewIdentifier} />
          </h3>
        </Col>
        <Col className="col-auto order-2 order-md-3 d-flex justify-content-end ">
          <CrewAwareItemsLink currentCrewIdentifier={currentCrewIdentifier} />
          <Button
            color="primary"
            className="me-2"
            active={isCrewDialogOpen}
            onClick={() => {
              setPlaylistAdvancedMenuOpen(false);
              setIsCrewDialogOpen((p) => !p);
            }}
            data-cy={CyApp.ShowCrewButton}
          >
            <FontAwesomeIcon icon={faUsers} className="pe-2" />
            {t("common.crew")}
          </Button>
          <Button
            color="primary"
            active={playlistAdvancedMenuOpen}
            onClick={() => {
              setPlaylistAdvancedMenuOpen((p) => !p);
              setIsCrewDialogOpen(false);
            }}
          >
            <FontAwesomeIcon icon={faGear} />
          </Button>
        </Col>
      </Row>

      <SettingsModal<PlaylistData>
        columns={columns}
        changeOrderBy={changeOrderBy}
        currentOrder={orderByAscOrDesc}
        currentField={orderByField}
        showDetails={showDetailsInPlaylistSummary}
        setShowDetails={setShowDetailsInPlaylistSummary}
        entryLimit={amountOfEntriesInPlaylistIndex}
        setEntryLimit={setAmountOfEntriesInPlaylistIndex}
        isModalOpen={playlistAdvancedMenuOpen}
        setIsModalOpen={setPlaylistAdvancedMenuOpen}
        limitOptions={[20, 50, 100, 1000]}
      />

      <CrewDialog
        currentCrewIdentifier={currentCrewIdentifier}
        setCurrentCrewIdentifier={setCurrentCrewIdentifier}
        isModalOpen={isCrewDialogOpen}
        setIsModalOpen={setIsCrewDialogOpen}
      />
    </>
  );
}
